import { getLocale } from 'i18n';
import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { createNewUser, checkINN, getDepartments } from 'service/AdminService'
import { postRegisterUser } from 'store/slices/AdminSlice'
import Notification from 'utils/Notifications'
import { ScaleLoader } from 'react-spinners'
import DepartmentsPanel from './Blocks/DepartmentsPanel'
import styles from './KomurSvyazPage.module.scss'
import {
  getNPAList, createNPA, deleteNPA, updateNPA, downloadNPA,
  getDocumentCategories,
  getNPAFolders, createNPAFolder, updateNPAFolder, deleteNPAFolder,
} from 'service/KomurConnectionService'

// ─────────────────────────────────────────────
// Inline registration form (no nested Layout)
// ─────────────────────────────────────────────
function RegisterTab() {
  useInterfaceLanguage();
  const dispatch = useDispatch()
  const [loader, setLoader] = useState(false)
  const [first_name, setFirst_name] = useState('')
  const [last_name, setLast_name] = useState('')
  const [middle_name, setMiddle_name] = useState('')
  const [number_phone, setNumber_phone] = useState('')
  const [inn, setInn] = useState('')
  const [position, setPosition] = useState('')
  const [department_id, setDepartment_id] = useState('')
  const [departments, setDepartments] = useState([])

  const [checkInnVal, setCheckInnVal] = useState('')
  const [innCheckResult, setInnCheckResult] = useState(null)
  const [innCheckLoading, setInnCheckLoading] = useState(false)

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  useEffect(() => {
    getDepartments().then(r => setDepartments(r.data)).catch(() => {})
  }, [])

  const validatePhone = (v) => v.length >= 10 && /^\d+$/.test(v)
  const validateINN = (v) => v && v.toString().length >= 10 && /^\d+$/.test(v)

  const validateField = (name, value) => {
    if (name === 'first_name' || name === 'last_name') {
      return !value || value.trim() === '' ? tr("Обязательное поле") : ''
    }
    if (name === 'number_phone') {
      if (!value) return tr("Обязательное поле")
      if (!validatePhone(value)) return tr("Минимум 10 цифр")
    }
    if (name === 'inn') {
      if (!value) return tr("Обязательное поле")
      if (!validateINN(value)) return tr("Минимум 10 цифр")
    }
    return ''
  }

  const handleBlur = (name, value) => {
    setTouched(t => ({ ...t, [name]: true }))
    setErrors(e => ({ ...e, [name]: validateField(name, value) }))
  }

  const handleCheckINN = async () => {
    if (!checkInnVal || checkInnVal.length < 10) {
      setNotify({ isOpen: true, message: tr("Введите корректный ИНН (минимум 10 цифр)"), type: 'warning', sound: 'warning' })
      return
    }
    try {
      setInnCheckLoading(true)
      const response = await checkINN(checkInnVal)
      setInnCheckResult(response.data)
    } catch {
      setNotify({ isOpen: true, message: tr("Ошибка при проверке ИНН"), type: 'error', sound: 'error' })
    } finally {
      setInnCheckLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const newErrors = {
      first_name: validateField('first_name', first_name),
      last_name: validateField('last_name', last_name),
      number_phone: validateField('number_phone', number_phone),
      inn: validateField('inn', inn),
    }
    setErrors(newErrors)
    setTouched({ first_name: true, last_name: true, number_phone: true, inn: true })
    if (Object.values(newErrors).some(err => err !== '')) {
      setNotify({ isOpen: true, message: tr("Пожалуйста, исправьте ошибки в форме"), type: 'warning', sound: 'warning' })
      return
    }
    try {
      setLoader(true)
      await createNewUser(first_name, last_name, middle_name, number_phone, inn, inn, true, position, department_id || null)
      dispatch(postRegisterUser({ first_name, last_name, middle_name, number_phone, inn }))
      setNotify({ isOpen: true, message: tr("Сотрудник успешно зарегистрирован!"), type: 'success', sound: 'success' })
      setFirst_name(''); setLast_name(''); setMiddle_name('')
      setNumber_phone(''); setInn(''); setPosition('')
      setDepartment_id(''); setErrors({}); setTouched({})
    } catch (error) {
      setNotify({ isOpen: true, message: error.response?.data?.message || tr("Ошибка регистрации. Попробуйте снова."), type: 'error', sound: 'error' })
    } finally {
      setLoader(false)
    }
  }

  return (
    <div className={styles.regContainer}>
      <div className={styles.regHeader}>
        <p className={styles.regTitle}>{tr("Регистрация сотрудника")}</p>
        <p className={styles.regSubtitle}>{tr("Заполните все поля для создания нового сотрудника")}</p>
      </div>

      {/* INN Check */}
      <div className={styles.innCheckSection}>
        <label className={styles.sectionLabel}>{tr("Проверка ИНН в базе данных")}</label>
        <div className={styles.innCheckWrapper}>
          <input
            type="text"
            className={styles.input}
            style={{ flex: '1 1 260px' }}
            placeholder={tr("Введите ИНН для проверки")}
            value={checkInnVal}
            onChange={e => { setCheckInnVal(e.target.value.replace(/\D/g, '')); setInnCheckResult(null) }}
            onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); handleCheckINN() } }}
            maxLength={14}
          />
          <button type="button" className={styles.checkButton} onClick={handleCheckINN} disabled={innCheckLoading}>
            {innCheckLoading ? tr("Проверка...") : tr("Проверить")}
          </button>
          {tr(innCheckResult && (
            <span className={`${styles.innResult} ${innCheckResult.exists ? styles.innExists : styles.innNotExists}`}>
              {tr(innCheckResult.message)}
            </span>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Personal */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>{tr("Личные данные")}</label>
          <div className={styles.formThreeCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>{tr("Фамилия *")}</label>
              <input type="text" className={`${styles.input} ${touched.last_name && errors.last_name ? styles.inputError : ''}`}
                placeholder={tr("Иванов")} value={last_name}
                onChange={e => setLast_name(e.target.value)} onBlur={() => handleBlur('last_name', last_name)} />
              {tr(touched.last_name && errors.last_name && <span className={styles.errorText}>{tr(errors.last_name)}</span>)}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>{tr("Имя *")}</label>
              <input type="text" className={`${styles.input} ${touched.first_name && errors.first_name ? styles.inputError : ''}`}
                placeholder={tr("Иван")} value={first_name}
                onChange={e => setFirst_name(e.target.value)} onBlur={() => handleBlur('first_name', first_name)} />
              {tr(touched.first_name && errors.first_name && <span className={styles.errorText}>{tr(errors.first_name)}</span>)}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>{tr("Отчество")}</label>
              <input type="text" className={styles.input} placeholder={tr("Иванович")}
                value={middle_name} onChange={e => setMiddle_name(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Contact + Position + Department */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>{tr("Контакты и должность")}</label>
          <div className={styles.formThreeCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>{tr("Номер телефона *")}</label>
              <input type="tel" className={`${styles.input} ${touched.number_phone && errors.number_phone ? styles.inputError : ''}`}
                placeholder="+996 XXX XXX XXX" value={number_phone}
                onChange={e => setNumber_phone(e.target.value.replace(/\D/g, ''))}
                onBlur={() => handleBlur('number_phone', number_phone)} />
              {tr(touched.number_phone && errors.number_phone && <span className={styles.errorText}>{tr(errors.number_phone)}</span>)}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>{tr("Должность")}</label>
              <input type="text" className={styles.input} placeholder={tr("Например: Инженер...")}
                value={position} onChange={e => setPosition(e.target.value)} />
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>{tr("Отдел")}</label>
              <select className={styles.input} value={department_id} onChange={e => setDepartment_id(e.target.value)}>
                <option value="">{tr("— Без отдела —")}</option>
                {departments.map(d => <option key={d.id} value={d.id}>{tr(d.name)}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* INN */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>{tr("Налоговая информация")}</label>
          <div className={styles.formTwoCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>{tr("ИНН *")}</label>
              <input type="text" className={`${styles.input} ${touched.inn && errors.inn ? styles.inputError : ''}`}
                placeholder={tr("Введите ИНН")} value={inn}
                onChange={e => setInn(e.target.value.replace(/\D/g, ''))}
                onBlur={() => handleBlur('inn', inn)} maxLength={14} />
              {tr(touched.inn && errors.inn && <span className={styles.errorText}>{tr(errors.inn)}</span>)}
              <span className={styles.helperText}>{tr("ИНН будет использован как пароль")}</span>
            </div>
          </div>
        </div>

        <div className={styles.submitSection}>
          {loader ? (
            <div className={styles.loaderWrapper}>
              <ScaleLoader color="var(--theme-primary-text, #0d00f3)" height={35} width={4} />
              <span className={styles.loaderText}>{tr("Регистрация...")}</span>
            </div>
          ) : (
            <button type="submit" className={styles.submitButton}>
              <span className={styles.submitIcon}>✓</span>{tr("Зарегистрировать сотрудника")}</button>
          )}
        </div>
      </form>
      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

// ─────────────────────────────────────────────
// Helpers for DocsAdminTab
// ─────────────────────────────────────────────
function admFmtDate(str) {
  if (!str) return '—'
  const d = new Date(str)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' })
}
function admFmtSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return tr("{{value0}} Б", { value0: bytes })
  if (bytes < 1048576) return tr("{{value0}} КБ", { value0: (bytes / 1024).toFixed(0) })
  return tr("{{value0}} МБ", { value0: (bytes / 1048576).toFixed(1) })
}
function admFileIcon(ext) {
  const e = (ext || '').toLowerCase()
  if (e === 'pdf') return '📕'
  if (['doc', 'docx'].includes(e)) return '📝'
  if (['xls', 'xlsx'].includes(e)) return '📊'
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(e)) return '🖼️'
  return '📄'
}

// ─────────────────────────────────────────────
// Docs / НПА Admin Tab
// ─────────────────────────────────────────────

const FOLDER_COLORS_NPA = [
  '#2563eb', '#0891b2', '#059669', '#d97706',
  '#dc2626', '#7c3aed', '#db2777', '#4338ca',
]
const NPA_ICONS = ['📁','📂','📋','📌','🗂️','🔐','📊','📝']

// Модал создания / редактирования папки
function FolderFormModal({ initial = {}, onSave, onClose }) {
  useInterfaceLanguage();
  const [name,  setName]  = useState(initial.name  || '')
  const [desc,  setDesc]  = useState(initial.description || '')
  const [icon,  setIcon]  = useState(initial.icon  || '📁')
  const [color, setColor] = useState(initial.color || FOLDER_COLORS_NPA[0])
  const isEdit = Boolean(initial.id)

  return (
    <div className={styles.admModalOverlay} onClick={onClose}>
      <div className={styles.admModal} onClick={e => e.stopPropagation()}>
        <div className={styles.admModalHead}>
          <span style={{ fontSize: 28 }}>{icon}</span>
          <h4 className={styles.admModalTitle}>{isEdit ? tr("Редактировать папку") : tr("Новая папка НПА")}</h4>
          <button className={styles.admModalClose} onClick={onClose}>✕</button>
        </div>

        <div className={styles.admModalField}>
          <label className={styles.docsUploadLabel}>{tr("Название *")}</label>
          <input className={styles.docsUploadInput} value={name} onChange={e => setName(e.target.value)}
            placeholder={tr("Название папки")} autoFocus />
        </div>
        <div className={styles.admModalField}>
          <label className={styles.docsUploadLabel}>{tr("Описание ")}<span className={styles.docsOptional}>{tr("(необязательно)")}</span></label>
          <input className={styles.docsUploadInput} value={desc} onChange={e => setDesc(e.target.value)}
            placeholder={tr("Краткое описание…")} />
        </div>
        <div className={styles.admModalField}>
          <label className={styles.docsUploadLabel}>{tr("Иконка")}</label>
          <div className={styles.admIconPicker}>
            {NPA_ICONS.map(ic => (
              <button key={ic}
                className={`${styles.admIconBtn} ${ic === icon ? styles.admIconBtnActive : ''}`}
                onClick={() => setIcon(ic)}>{tr(ic)}</button>
            ))}
          </div>
        </div>
        <div className={styles.admModalField}>
          <label className={styles.docsUploadLabel}>{tr("Цвет")}</label>
          <div className={styles.admColorPicker}>
            {FOLDER_COLORS_NPA.map(c => (
              <button key={c}
                className={`${styles.admColorBtn} ${c === color ? styles.admColorBtnActive : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        <div className={styles.docsUploadActions}>
          <button className={styles.docsConfirmBtn}
            onClick={() => name.trim() && onSave({ name: name.trim(), description: desc.trim(), icon, color })}
            disabled={!name.trim()}>
            {isEdit ? tr("Сохранить") : tr("Создать")}
          </button>
          <button className={styles.docsCancelBtn} onClick={onClose}>{tr("Отмена")}</button>
        </div>
      </div>
    </div>
  )
}

function DocsAdminTab() {
  useInterfaceLanguage();
  const [allDocs,     setAllDocs]     = useState(null)
  const [folders,     setFolders]     = useState(null)
  const [categories,  setCategories]  = useState([])
  const [openFolder,  setOpenFolder]  = useState(null)  // folder object
  const [uploadPending, setUploadPending] = useState(null)
  const [uploading,   setUploading]   = useState(false)
  const [editingId,   setEditingId]   = useState(null)
  const [editTitle,   setEditTitle]   = useState('')
  const [folderModal, setFolderModal] = useState(null)   // null | 'new' | folderObj
  const [notify,      setNotify]      = useState({ isOpen: false, message: '', type: '' })
  const fileInputRef = useRef()

  const reload = () => {
    getNPAList().then(r => setAllDocs(r.data)).catch(() => setAllDocs([]))
    getNPAFolders().then(r => setFolders(r.data)).catch(() => setFolders([]))
  }

  useEffect(() => {
    reload()
    getDocumentCategories().then(r => setCategories(r.data)).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Файл выбран ─────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadPending({
      file,
      title: file.name.replace(/\.[^/.]+$/, ''),
      desc: '',
      categoryId: '',
      folderId: openFolder ? String(openFolder.id) : '',
    })
    e.target.value = ''
  }

  const handleUploadConfirm = async () => {
    if (!uploadPending?.title.trim()) return
    const { file, title, desc, categoryId, folderId } = uploadPending
    const fd = new FormData()
    fd.append('file', file)
    fd.append('title', title.trim())
    if (desc.trim()) fd.append('description', desc.trim())
    if (categoryId)  fd.append('category', categoryId)
    if (folderId)    fd.append('folder', folderId)
    fd.append('is_active', 'true')
    setUploading(true)
    try {
      const r = await createNPA(fd)
      setAllDocs(prev => [r.data, ...(prev || [])])
      if (folderId) {
        setFolders(prev => prev?.map(f =>
          String(f.id) === String(folderId) ? { ...f, doc_count: (f.doc_count || 0) + 1 } : f
        ))
      }
      setUploadPending(null)
      setNotify({ isOpen: true, message: tr("НПА успешно загружен"), type: 'success' })
    } catch {
      setNotify({ isOpen: true, message: tr("Ошибка при загрузке файла"), type: 'error' })
    }
    setUploading(false)
  }

  // ── Удаление документа ─────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm(tr("Удалить НПА? Действие необратимо."))) return
    const doc = allDocs?.find(d => d.id === id)
    try {
      await deleteNPA(id)
      setAllDocs(prev => prev.filter(d => d.id !== id))
      if (doc?.folder) {
        setFolders(prev => prev?.map(f =>
          f.id === doc.folder ? { ...f, doc_count: Math.max(0, (f.doc_count || 1) - 1) } : f
        ))
      }
    } catch {
      setNotify({ isOpen: true, message: tr("Ошибка при удалении"), type: 'error' })
    }
  }

  const handleEditSave = async (id) => {
    if (!editTitle.trim()) return
    try {
      const r = await updateNPA(id, { title: editTitle.trim() })
      setAllDocs(prev => prev.map(d => d.id === id ? { ...d, title: r.data.title } : d))
      setEditingId(null)
    } catch {
      setNotify({ isOpen: true, message: tr("Ошибка при сохранении"), type: 'error' })
    }
  }

  const handleDownload = (id, fileName) => {
    downloadNPA(id).then(r => {
      const a = document.createElement('a')
      a.href = r.data.file_url
      a.download = fileName || 'document'
      a.click()
    }).catch(() => {})
  }

  // ── Папки CRUD ────────────────────────────────────────────────────
  const handleFolderSave = async (data) => {
    try {
      if (folderModal === 'new') {
        const r = await createNPAFolder(data)
        setFolders(prev => [...(prev || []), { ...r.data, doc_count: 0 }])
        setNotify({ isOpen: true, message: tr("Папка создана"), type: 'success' })
      } else {
        const r = await updateNPAFolder(folderModal.id, data)
        setFolders(prev => prev?.map(f => f.id === folderModal.id ? { ...f, ...r.data } : f))
        if (openFolder?.id === folderModal.id) setOpenFolder(f => ({ ...f, ...r.data }))
        setNotify({ isOpen: true, message: tr("Папка обновлена"), type: 'success' })
      }
      setFolderModal(null)
    } catch {
      setNotify({ isOpen: true, message: tr("Ошибка при сохранении папки"), type: 'error' })
    }
  }

  const handleFolderDelete = async (folder) => {
    if (!window.confirm(tr("Удалить папку \"​{{value0}}​\"? Документы останутся без папки.", { value0: folder.name }))) return
    try {
      await deleteNPAFolder(folder.id)
      setFolders(prev => prev?.filter(f => f.id !== folder.id))
      setAllDocs(prev => prev?.map(d =>
        d.folder === folder.id ? { ...d, folder: null, folder_name: '' } : d
      ))
      if (openFolder?.id === folder.id) setOpenFolder(null)
    } catch {
      setNotify({ isOpen: true, message: tr("Ошибка при удалении папки"), type: 'error' })
    }
  }

  // ── Данные ─────────────────────────────────────────────────────────────
  const folderDocs  = openFolder ? (allDocs?.filter(d => d.folder === openFolder.id) || []) : null
  const looseDocs   = allDocs?.filter(d => !d.folder) || []
  const totalDl     = allDocs?.reduce((s, d) => s + (d.download_count || 0), 0) ?? 0
  const totalViews  = allDocs?.reduce((s, d) => s + (d.view_count    || 0), 0) ?? 0

  // ── Таблица документов ────────────────────────────────────────────────
  const DocTable = ({ docs, emptyText }) => { useInterfaceLanguage(); return (<>
      {tr(!docs && <p className={styles.docsLoading}>{tr("Загрузка…")}</p>)}
      {tr(docs && !docs.length && (
        <div className={styles.docsEmpty}>
          <span className={styles.docsEmptyIcon}>📂</span>
          <p className={styles.docsEmptyTitle}>{tr(emptyText || tr("Нет документов"))}</p>
        </div>
      ))}
      {tr(docs && docs.length > 0 && (
        <div className={styles.docsTableWrap}>
          <table className={styles.docsTable}>
            <thead><tr>
              <th>{tr("Документ")}</th>
              <th>{tr("Категория")}</th>
              <th>{tr("Папка")}</th>
              <th>{tr("Размер")}</th>
              <th>{tr("Дата")}</th>
              <th>⬇</th>
              <th>👁</th>
              <th></th>
            </tr></thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id} className={styles.docsRow}>
                  <td className={styles.docsFileCell}>
                    <span className={styles.docsFileIcon}>{admFileIcon(doc.file_extension)}</span>
                    {editingId === doc.id ? (
                      <input
                        className={styles.docsInlineInput}
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleEditSave(doc.id)
                          if (e.key === 'Escape') setEditingId(null)
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className={styles.docsFileName}>{tr(doc.title)}</span>
                    )}
                    {tr(doc.file_extension && (
                      <span className={styles.docsExtBadge}>{doc.file_extension.toUpperCase()}</span>
                    ))}
                  </td>
                  <td>
                    {doc.category_name
                      ? <span className={styles.docsCatBadge}>{tr(doc.category_name)}</span>
                      : <span className={styles.docsNoCat}>—</span>}
                  </td>
                  <td>
                    {doc.folder_name
                      ? <span className={styles.docsFolderBadge}>📁 {tr(doc.folder_name)}</span>
                      : <span className={styles.docsNoCat}>—</span>}
                  </td>
                  <td className={styles.docsMeta}>{admFmtSize(doc.file_size)}</td>
                  <td className={styles.docsMeta}>{admFmtDate(doc.created_at)}</td>
                  <td className={styles.docsCountCell}>{tr(doc.download_count ?? 0)}</td>
                  <td className={styles.docsCountCell}>{tr(doc.view_count ?? 0)}</td>
                  <td className={styles.docsRowActions}>
                    {editingId === doc.id ? (
                      <>
                        <button className={styles.docsSaveBtn} onClick={() => handleEditSave(doc.id)} title={tr("Сохранить")}>✓</button>
                        <button className={styles.docsCancelIconBtn} onClick={() => setEditingId(null)} title={tr("Отмена")}>✕</button>
                      </>
                    ) : (
                      <>
                        <button className={styles.docsEditBtn}
                          onClick={() => { setEditingId(doc.id); setEditTitle(doc.title) }}
                          title={tr("Редактировать")}>✏️</button>
                        <button className={styles.docsDlBtn}
                          onClick={() => handleDownload(doc.id, doc.file_name)}
                          title={tr("Скачать")}>⬇</button>
                        <button className={styles.docsDelBtn}
                          onClick={() => handleDelete(doc.id)}
                          title={tr("Удалить")}>🗑</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>); }

  // ── Форма загрузки ────────────────────────────────────────────────────
  const UploadForm = () => { useInterfaceLanguage(); return (<div className={styles.docsUploadForm}>
      <div className={styles.docsUploadTop}>
        <span className={styles.docsUploadIcon}>{admFileIcon(uploadPending.file.name.split('.').pop())}</span>
        <span className={styles.docsUploadFilename}>{tr(uploadPending.file.name)}</span>
        <span className={styles.docsUploadFilesize}>{admFmtSize(uploadPending.file.size)}</span>
      </div>
      <div className={styles.docsUploadFields}>
        <div className={styles.docsUploadField}>
          <label className={styles.docsUploadLabel}>{tr("Название НПА *")}</label>
          <input className={styles.docsUploadInput}
            value={uploadPending.title}
            onChange={e => setUploadPending(p => ({ ...p, title: e.target.value }))}
            placeholder={tr("Название документа")}
            autoFocus />
        </div>
        <div className={styles.docsUploadField}>
          <label className={styles.docsUploadLabel}>{tr("Категория")}</label>
          <select className={styles.docsUploadInput}
            value={uploadPending.categoryId}
            onChange={e => setUploadPending(p => ({ ...p, categoryId: e.target.value }))}>
            <option value="">{tr("— Без категории —")}</option>
            {categories.map(c => <option key={c.id} value={c.id}>{tr(c.icon)} {tr(c.name)}</option>)}
          </select>
        </div>
        <div className={styles.docsUploadField}>
          <label className={styles.docsUploadLabel}>{tr("Папка")}</label>
          <select className={styles.docsUploadInput}
            value={uploadPending.folderId}
            onChange={e => setUploadPending(p => ({ ...p, folderId: e.target.value }))}>
            <option value="">{tr("— Без папки —")}</option>
            {(folders || []).map(f => <option key={f.id} value={f.id}>{tr(f.icon)} {tr(f.name)}</option>)}
          </select>
        </div>
        <div className={styles.docsUploadFieldFull}>
          <label className={styles.docsUploadLabel}>{tr("Описание ")}<span className={styles.docsOptional}>{tr("(необязательно)")}</span></label>
          <input className={styles.docsUploadInput}
            value={uploadPending.desc}
            onChange={e => setUploadPending(p => ({ ...p, desc: e.target.value }))}
            placeholder={tr("Краткое описание…")} />
        </div>
      </div>
      <div className={styles.docsUploadActions}>
        <button className={styles.docsConfirmBtn}
          onClick={handleUploadConfirm}
          disabled={uploading || !uploadPending.title.trim()}>
          {uploading ? tr("⏳ Загрузка…") : tr("✓ Загрузить")}
        </button>
        <button className={styles.docsCancelBtn}
          onClick={() => setUploadPending(null)}
          disabled={uploading}>{tr("Отмена")}</button>
      </div>
    </div>); }

  // ── Рендер: открытая папка ───────────────────────────────────────────
  if (openFolder) {
    return (
      <div className={styles.docsAdmin}>
        <div className={styles.docsAdminHeader}>
          <button className={styles.admBackBtn} onClick={() => setOpenFolder(null)}>{tr("‹ Назад")}</button>
          <div className={styles.admFolderViewTitle}>
            <span style={{ color: openFolder.color, fontSize: 24 }}>{tr(openFolder.icon)}</span>
            <div>
              <p className={styles.docsAdminTitle}>{tr(openFolder.name)}</p>
              {tr(openFolder.description && <p className={styles.docsAdminSub}>{tr(openFolder.description)}</p>)}
            </div>
          </div>
          <button className={styles.admEditFolderBtn} onClick={() => setFolderModal(openFolder)}>{tr("✏️ Редактировать")}</button>
          {tr(!uploadPending && (
            <button className={styles.docsUploadBtn} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <span>+</span>{tr(" Загрузить в папку")}</button>
          ))}
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
        </div>
        {tr(uploadPending && <UploadForm />)}
        <DocTable docs={folderDocs} emptyText={tr("Папка пуста")} />
        {tr(folderModal && (
          <FolderFormModal
            initial={typeof folderModal === 'object' && folderModal !== 'new' ? folderModal : {}}
            onSave={handleFolderSave}
            onClose={() => setFolderModal(null)}
          />
        ))}
        <Notification notify={notify} setNotify={setNotify} />
      </div>
    )
  }

  // ── Рендер: главный вид ───────────────────────────────────────────────
  return (
    <div className={styles.docsAdmin}>

      {/* ── Header ── */}
      <div className={styles.docsAdminHeader}>
        <div className={styles.docsAdminTitleGroup}>
          <p className={styles.docsAdminTitle}>{tr("Управление НПА")}</p>
          <p className={styles.docsAdminSub}>{tr("Нормативно-правовые акты — видны всем сотрудникам")}</p>
        </div>
        <button className={styles.admNewFolderBtn} onClick={() => setFolderModal('new')}>{tr("📁 Новая папка")}</button>
        {tr(!uploadPending && (
          <button className={styles.docsUploadBtn} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            <span>+</span>{tr(" Загрузить НПА")}</button>
        ))}
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
      </div>

      {/* ── Stats ── */}
      <div className={styles.docsStats}>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{tr((folders || []).length)}</span>
          <span className={styles.docsStatLabel}>{tr("папок")}</span>
        </div>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{tr(allDocs?.length ?? '…')}</span>
          <span className={styles.docsStatLabel}>{tr("документов")}</span>
        </div>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{tr(totalDl)}</span>
          <span className={styles.docsStatLabel}>{tr("скачиваний")}</span>
        </div>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{tr(totalViews)}</span>
          <span className={styles.docsStatLabel}>{tr("просмотров")}</span>
        </div>
      </div>

      {/* ── Upload form ── */}
      {tr(uploadPending && <UploadForm />)}

      {/* ── Папки ── */}
      {tr(folders !== null && folders.length > 0 && (
        <div className={styles.admFoldersGrid}>
          {folders.map(folder => (
            <div
              key={folder.id}
              className={styles.admFolderCard}
              style={{ '--fc': folder.color || '#2563eb' }}
            >
              <div className={styles.admFolderCardMain}
                onClick={() => setOpenFolder(folder)}
                role="button" tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setOpenFolder(folder)}>
                <div className={styles.admFolderIcon}>{tr(folder.icon || '📁')}</div>
                <div className={styles.admFolderInfo}>
                  <span className={styles.admFolderName}>{tr(folder.name)}</span>
                  {tr(folder.description && <span className={styles.admFolderDesc}>{tr(folder.description)}</span>)}
                </div>
              </div>
              <div className={styles.admFolderRight}>
                <span className={styles.admFolderCount}>
                  {tr(folder.doc_count)}{' '}
                  {folder.doc_count === 1 ? tr("документ") : folder.doc_count >= 2 && folder.doc_count <= 4 ? tr("документа") : tr("документов")}
                </span>
                <button className={styles.admFolderEditBtn}
                  onClick={e => { e.stopPropagation(); setFolderModal(folder) }}
                  title={tr("Редактировать")}>✏️</button>
                <button className={styles.admFolderDeleteBtn}
                  onClick={e => { e.stopPropagation(); handleFolderDelete(folder) }}
                  title={tr("Удалить")}>✕</button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* ── Документы без папки ── */}
      {tr(folders !== null && folders.length > 0 && (
        <div className={styles.admLooseHeader}>
          <span>📄</span>
          <span className={styles.admLooseTitle}>{tr("Без папки")}</span>
          <span className={styles.admLooseCount}>{tr(looseDocs.length)}</span>
        </div>
      ))}
      <DocTable
        docs={allDocs === null ? null : looseDocs}
        emptyText={tr("Нет документов— нажмите «Загрузить НПА» чтобы добавить первый документ")}
      />

      {/* Модал папки */}
      {tr(folderModal && (
        <FolderFormModal
          initial={folderModal === 'new' ? {} : folderModal}
          onSave={handleFolderSave}
          onClose={() => setFolderModal(null)}
        />
      ))}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

// ─────────────────────────────────────────────
// Main KomurSvyazPage
// ─────────────────────────────────────────────
const TABS = [
  { key: 'departments', icon: '🏢', label: "Отделы" },
  { key: 'register',    icon: '👤', label: "Регистрация сотрудников" },
  { key: 'docs',        icon: '📚', label: "Документы / НПА" },
]

const KomurSvyazPage = () => {
  useInterfaceLanguage();
  const [activeTab, setActiveTab] = useState('departments')

  return (
    <div className={styles.page}>
        <div className={styles.tabs}>
          {TABS.map(({ key, icon, label }) => (
            <button
              key={key}
              className={`${styles.tab} ${activeTab === key ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <span className={styles.tabIcon}>{icon}</span>
              {tr(label)}
            </button>
          ))}
        </div>

        {tr(activeTab === 'departments' && <DepartmentsPanel />)}
        {tr(activeTab === 'register' && <RegisterTab />)}
        {tr(activeTab === 'docs' && <DocsAdminTab />)}
      </div>
  )
}

export default KomurSvyazPage
